import { ApiClient } from '@/lib/api-client';
import { 
  MealPlan,
  MealPlanWithItems,
  MealPlanItem,
  CreateMealPlanRequest,
  AddMealPlanItemRequest,
  UpdateMealPlanItemRequest,
  MealPlanStats,
  ShoppingList
} from '@/types/api';
import { getWeekStart } from '@/utils/dateUtils';

export class MealPlansService {
  /**
   * Create a new meal plan
   */
  static async createMealPlan(data: CreateMealPlanRequest): Promise<MealPlan> {
    const response = await ApiClient.post<MealPlan>('/meal-plans', data);
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.message || 'Failed to create meal plan');
  }

  /**
   * Get user's meal plans (paginated)
   */
  static async getMealPlans(page: number = 1, limit: number = 10): Promise<{
    mealPlans: MealPlan[];
    total: number;
    totalPages: number;
  }> {
    const response = await ApiClient.get<{
      mealPlans: MealPlan[];
      total: number;
      totalPages: number;
    }>(`/meal-plans?page=${page}&limit=${limit}`);
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.message || 'Failed to get meal plans');
  }

  /**
   * Get meal plan by ID with items
   */
  static async getMealPlanById(id: string): Promise<MealPlanWithItems> {
    const response = await ApiClient.get<MealPlanWithItems>(`/meal-plans/${id}`);
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.message || 'Failed to get meal plan');
  }

  /**
   * Update meal plan
   */
  static async updateMealPlan(id: string, data: Partial<CreateMealPlanRequest>): Promise<MealPlan> {
    const response = await ApiClient.put<MealPlan>(`/meal-plans/${id}`, data);
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.message || 'Failed to update meal plan');
  }

  /**
   * Delete meal plan
   */
  static async deleteMealPlan(id: string): Promise<void> {
    const response = await ApiClient.delete(`/meal-plans/${id}`);
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to delete meal plan');
    }
  }

  /**
   * Add recipe to meal plan
   */
  static async addMealPlanItem(mealPlanId: string, data: AddMealPlanItemRequest): Promise<MealPlanItem> {
    const response = await ApiClient.post<MealPlanItem>(`/meal-plans/${mealPlanId}/items`, data);
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.message || 'Failed to add recipe to meal plan');
  }

  /**
   * Update meal plan item (servings)
   */
  static async updateMealPlanItem(itemId: string, data: UpdateMealPlanItemRequest): Promise<MealPlanItem> {
    const response = await ApiClient.put<MealPlanItem>(`/meal-plans/items/${itemId}`, data);
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.message || 'Failed to update meal plan item');
  }

  /**
   * Remove meal plan item
   */
  static async removeMealPlanItem(itemId: string): Promise<void> {
    const response = await ApiClient.delete(`/meal-plans/items/${itemId}`);
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to remove meal plan item');
    }
  }

  /**
   * Generate shopping list from meal plan
   */
  static async generateShoppingList(mealPlanId: string): Promise<ShoppingList> {
    const response = await ApiClient.get<ShoppingList>(`/meal-plans/${mealPlanId}/shopping-list`);
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.message || 'Failed to generate shopping list');
  }

  /**
   * Get meal plan statistics
   */
  static async getMealPlanStats(mealPlanId: string): Promise<MealPlanStats> {
    const response = await ApiClient.get<MealPlanStats>(`/meal-plans/${mealPlanId}/stats`);
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.message || 'Failed to get meal plan stats');
  }

  /**
   * Get meal plan for specific week (utility method)
   */
  static async getMealPlanByWeek(weekStart: string): Promise<MealPlanWithItems | null> {
    try {
      const ymd = new Date(weekStart).toISOString().split('T')[0];
      const response = await ApiClient.get<MealPlanWithItems | null>(`/meal-plans/by-week?weekStart=${ymd}`);
      if (response.success) {
        return response.data ?? null;
      }
      return null;
    } catch (error) {
      console.error('Error getting meal plan by week:', error);
      return null;
    }
  }

  /**
   * Get or create meal plan for current week
   */
  static async getCurrentWeekMealPlan(): Promise<MealPlanWithItems> {
    // Use getWeekStart() from dateUtils — handles all days including Sunday correctly
    const monday = getWeekStart();
    const weekStart = monday.toISOString();

    // Try to get existing meal plan for this week
    const existingPlan = await this.getMealPlanByWeek(weekStart);
    
    if (existingPlan) {
      return existingPlan;
    }

    // Create new meal plan for this week
    const newPlan = await this.createMealPlan({ weekStart });
    return await this.getMealPlanById(newPlan.id);
  }
}
